import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
  inject,
  input,
  computed,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { DataTestDirective, OUTLAW_QUERY_PARAMS, TargetUrl } from '@outlaw/core';
import { FullScreenPlayService } from '../../../../services/util/full-screen-play.service';
import {
  CommunityItem,
  IPartnerService,
  PostTypes,
  SocialMediaPlatform,
  VideoAccess,
  VideoPlayerData,
  WatchProgress,
} from '../../../../interfaces';
import { CommunityMobileEventsService } from '../../../../services/util/mobile-events.service';
import { firstValueFrom, Observable, of } from 'rxjs';
import { VideoService } from '../../../../services/api/video.service';
import { catchError, map, startWith, tap } from 'rxjs/operators';
import { CommunityNavigationService, FeatureFlagService, GuestAuthService } from '../../../../services';
import { StreamComponent } from '../../../stream/stream.component';
import { CommunityLoaderComponent } from '../../../community-loader/community-loader.component';
import { AsyncPipe } from '@angular/common';
import { UnixDateFormatterPipe } from '../../../../pipes/unix-date-formatter.pipe';
import { FormatLineBreaksPipe } from '../../../../pipes/format-line-breaks.pipe';
import { PartnerServiceFactory } from '../../../../services/partner-services/partner-service.factory';
import { GtagCommunityService } from '../../../../services/analytics/gta-community.service';
import { IconComponent } from '@outlaw/icons';
import { extractFlixsnipType } from '../../../../utils';

interface VideoAccessState {
  loading: boolean;
  access: VideoAccess | null;
}

@Component({
  selector: 'app-post-video',
  templateUrl: './post-video.component.html',
  styleUrls: ['./post-video.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    StreamComponent,
    CommunityLoaderComponent,
    AsyncPipe,
    UnixDateFormatterPipe,
    FormatLineBreaksPipe,
    DataTestDirective,
    IconComponent,
  ],
})
export class PostVideoComponent implements OnInit, AfterViewInit, OnDestroy {
  item = input<CommunityItem>();
  isGuest = input<boolean>(false);
  isMajiApp = input<boolean>(false);
  variant = input<'primary' | 'secondary'>('primary');
  openInSameWindow = input<boolean>(false);

  navigateToPost = output<void>();

  @ViewChild('videoPlayerVisibility') videoPlayerVisibility: ElementRef;
  @ViewChild('watchButton') watchButton: ElementRef;

  videoAccessState$: Observable<VideoAccessState>;
  videoAccess: VideoAccess | null = null;

  trailerToken: string;
  trailerVideoToken: string;
  videoToken: string;
  videoLink: string;
  mutedTrailer = true;
  trailerPromoPlay = true;
  showLoader = false;

  postUrl = computed(() => {
    return this.variant() === 'primary' ? this.navigationService.getPostUrl(this.item().post_uuid) : null;
  });

  target = computed(() => {
    return this.openInSameWindow() ? TargetUrl.sameWindow : TargetUrl.newWindow;
  });

  private observer: IntersectionObserver;

  get videoId(): number {
    return this.item().content.videos?.[0]?.video_external_id;
  }

  get requiresPurchase(): boolean {
    return !this.isGuest() && this.videoAccess && !this.videoAccess?.video_access_options?.have_access;
  }

  get isPaywallEnabled(): boolean {
    return this.featureFlagService.isPaywallEnabledForPlatform(this.item());
  }

  get canDisplayWatchButton(): boolean {
    const canShowWatchButtonByAccess = this.videoAccess?.video_access_options?.have_access;

    return this.isGuest() || !this.videoId || canShowWatchButtonByAccess || !this.isPaywallEnabled;
  }

  get canDisplayPostContent(): boolean {
    return Boolean(this.item().content.title || this.item().content.text || this.requiresPurchase);
  }

  private videoService = inject(VideoService);
  private fullScreenPlayService = inject(FullScreenPlayService);
  private mobileEventsService = inject(CommunityMobileEventsService);
  private navigationService = inject(CommunityNavigationService);
  private featureFlagService = inject(FeatureFlagService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private partnerServiceFactory = inject(PartnerServiceFactory);
  private gtagCommunityService = inject(GtagCommunityService);
  private guestAuthService = inject(GuestAuthService);

  private partnerService: IPartnerService;

  ngOnInit(): void {
    this.videoToken = this.item().content.videos?.[0]?.video;
    this.trailerToken = this.item().content.videos?.[0]?.trailer || this.videoToken;

    this.videoLink = this.item().content.videos?.[0]?.video_url;

    this.checkVideoAccess(this.videoId, this.isGuest());

    this.fullScreenPlayService.isActivePlay.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(videoData => {
      this.trailerPromoPlay = !videoData;
      this.showLoader = false;
    });

    this.partnerService = this.partnerServiceFactory.getService(this.item().original_platform.slug);
  }

  ngAfterViewInit(): void {
    const options: IntersectionObserverInit = {
      rootMargin: '0px',
      threshold: 0.1,
    };

    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        this.trailerVideoToken = this.trailerToken;
      } else {
        this.trailerVideoToken = null;
      }

      this.cdr.markForCheck();
    }, options);

    if (this.videoPlayerVisibility) {
      this.observer.observe(this.videoPlayerVisibility.nativeElement);
    }
  }

  watchClick(event: Event, videoToken: string): void {
    event.stopPropagation();

    if (this.isGuest()) {
      const videoId = this.videoId;

      this.guestAuthService.handleGuestAuth({
        filmId: videoId ? videoId.toString() : undefined,
        postId: this.item().post_uuid,
      });
      return;
    }

    if (!this.featureFlagService.isCommunityInApp()) {
      this.gtagCommunityService.watchButtonClick(this.item().post_uuid);
    }

    if (this.showLoader) {
      return;
    }

    // Handle Flixsnip platform videos
    if (this.item().original_platform?.slug === SocialMediaPlatform.Flixsnip) {
      const media = this.item().content.social_media;
      const id = this.item().content.social_media.external_id;
      const type = extractFlixsnipType(media.external_url);

      // Send event to mobile app
      this.mobileEventsService.openFlixsnipVideo({
        videoLink: this.videoLink,
        id,
        type,
      });
      return;
    }

    if (this.variant() === 'primary' && !this.mobileEventsService.isWebView) {
      if (this.featureFlagService.isCommunityInApp()) {
        this.navigationService.openExternalUrl(this.videoLink);
        return;
      }

      this.navigationService.navigateToPost(
        this.item().post_uuid,
        this.openInSameWindow() ? TargetUrl.sameWindow : TargetUrl.newWindow
      );
      return;
    }

    const token = videoToken || this.videoToken;

    if (this.mobileEventsService.isWebView && this.isMajiApp() && token) {
      this.mobileEventsService.openVideo({
        token,
        title: this.item().content.title || '',
      });
      return;
    }

    if ((this.item().post_type.slug === PostTypes.postedVideo || (this.item().post_type.slug === PostTypes.post && this.item().content.videos?.length)) && token) {
      this.playVideo(event, token);
      return;
    }

    if (this.mobileEventsService.isWebView) {
      this.openMobileUrl(event, this.videoLink);
      return;
    }

    if (this.videoId && this.isPaywallEnabled) {
      if (this.featureFlagService.isCommunityInApp()) {
        this.navigationService.openExternalUrl(this.videoLink);
        return;
      }

      if (videoToken) {
        this.playVideo(event, videoToken);
        return;
      }
    }

    window.open(this.videoLink, '_blank');
  }

  async playVideo(event?: Event, streamUrl?: string): Promise<void> {
    const playData: VideoPlayerData = {
      title: this.item().content.title,
      streamUrl: streamUrl || this.videoLink,
      thumbnail: this.item().content?.videos?.[0]?.preview_url,
    };

    if (this.mobileEventsService.isAndroidWebView) {
      this.handleAndroidPlay(event, playData);
      return;
    }

    this.fullScreenPlayService.startFullScreenPlay(playData);
  }

  openMobileUrl(e: Event, link: string): void {
    e.preventDefault();
    this.mobileEventsService.openUrl(link);
  }

  onSubscribeClick(event: Event): void {
    event.stopPropagation();

    if (this.mobileEventsService.isWebView && this.isMajiApp()) {
      this.mobileEventsService.openSubscribe();
      return;
    }

    if (this.variant() === 'primary') {
      const postLink = this.navigationService.getPostUrl(this.item().post_uuid);
      history.pushState({}, '', postLink);
    }

    this.navigationService.navigateToSubscription(
      {
        [OUTLAW_QUERY_PARAMS.filmID]: this.videoId.toString(),
        [OUTLAW_QUERY_PARAMS.postId]: this.item().post_uuid,
      },
      this.openInSameWindow() ? TargetUrl.sameWindow : TargetUrl.newWindow
    );
  }

  async addWatchProgress(progress: number) {
    if (this.isGuest() || !this.partnerService) {
      return;
    }

    const watchProgress: WatchProgress = { progress };

    await firstValueFrom(this.partnerService.addWatchProgressToVideo(this.videoId, watchProgress));
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private handleAndroidPlay(event: Event, { streamUrl, title }: VideoPlayerData): void {
    event.preventDefault();
    this.mobileEventsService.openPlayer({ token: streamUrl || this.videoLink, title });
  }

  private checkVideoAccess(videoId: number, isGuest: boolean) {
    const isPaywallEnabled = this.featureFlagService.isPaywallEnabledForPlatform(this.item());

    this.videoAccessState$ =
      videoId && !isGuest && isPaywallEnabled
        ? this.videoService.hasVideoAccess(videoId).pipe(
            map(videoAccess => {
              this.videoAccess = videoAccess;

              return {
                loading: false,
                access: {
                  ...videoAccess,
                },
              };
            }),
            startWith({ loading: true, access: null }),
            catchError(() => of({ loading: false, access: null }))
          )
        : of({ loading: false, access: null }).pipe(
            tap(() => {
              this.videoAccess = null;
            })
          );
  }

  onPostClick(event: Event): void {
    if (this.openInSameWindow()) {
      event.preventDefault();
      this.navigateToPost.emit();
    }
  }
}
